/**
 * ScheduledScanPanel — Multi-schedule UI (F-Strategies-MultiSchedule).
 *
 * A strategy can now have N independent schedules, each with its own
 * (watchlist, intervalMinutes, scheduleType, scheduleValue, routing).
 * The panel renders one editor per existing schedule plus an "Add
 * Schedule" affordance that appends a draft row. Each row owns its own
 * save / toggle / delete state.
 *
 * onChange fires whenever ANY schedule for the strategy changes state
 * (save/toggle/delete) so the parent tile chip can recompute "is at
 * least one schedule active?" without re-fetching.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box, Card, CardContent, CardHeader, Typography, Switch, FormControlLabel,
  FormControl, InputLabel, Select, MenuItem, TextField, Button, Chip,
  Grid, CircularProgress, Divider, Alert, Tooltip, FormHelperText,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import PauseIcon from '@mui/icons-material/Pause';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import toast from 'react-hot-toast';
import {
  strategyScanService, ScheduledScan, ScheduleType, Watchlist, UpsertSchedulePayload,
} from '../../services/strategyScan.service';
import type { ScanCadence } from '../../services/customInterval.service';
import { cadenceValueMax, isScanEligible, DAY } from '../../utils/intervalCatalog';

interface Props {
  strategyId: number;
  watchlists: Watchlist[];
  /** Shown intervals (minutes) — user's analytical timeframe. */
  intervalOptions: number[];
  minutesLabel: (m: number) => string;
  /** The TICK cadence that covers every close of an interval, when the app can
   *  compute one. Supplied for custom intervals; undefined for builtins, which
   *  are all day-divisors and whose cadence simply equals the bar.
   *
   *  The two are NOT the same question. 420m (7H) does not divide 1440, so no
   *  cadence equals it — such a schedule ticks on a finer grid and the engine
   *  emits once per CLOSED bar, deduped by candleTime. */
  cadenceFor?: (minutes: number, exchanges: string[]) => ScanCadence | undefined;
  /** Set when the selected strategy's ENGINE refuses to place orders at all, in
   *  which case the Auto-Trade switch is a lie: it looks armed, the schedule
   *  stores true, and the engine ignores it. The string is the reason, shown to
   *  the user. OM_Simple is the case this exists for — it is always in the
   *  market with no stop of its own, so it never hands a signal to TradeDesk. */
  autoTradeRefusedReason?: string;
  /** Called whenever any schedule for this strategy changes state. The
   *  parent uses this to update its tile chip. The payload is the FIRST
   *  schedule (for backward compat) plus the full list. */
  onChange?: (first: ScheduledScan | null, all: ScheduledScan[]) => void;
}

function formatTs(ts?: string | null) {
  if (!ts) return '—';
  return new Date(ts).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata', hour12: false });
}

/** The same caps the server validates against — imported, not re-typed, so the
 *  editor cannot clamp to a bound the server disagrees with. */
const cadenceMax = (t: ScheduleType): number => cadenceValueMax(t as 'MINUTE' | 'HOUR' | 'DAY');

function clampCadence(v: number, t: ScheduleType): number {
  const n = Math.round(Number(v));
  if (!Number.isFinite(n) || n < 1) return 1;
  return Math.min(n, cadenceMax(t));
}

/** A cadence in words: "every 30 min", "every 4 hours", "once a day". */
function cadenceWords(c?: ScanCadence): string {
  if (!c) return '';
  if (c.scheduleType === 'DAY') return 'once a day';
  if (c.scheduleType === 'HOUR') return c.scheduleValue === 1 ? 'every hour' : `every ${c.scheduleValue} hours`;
  return `every ${c.scheduleValue} min`;
}

// Per-schedule colour palette (cycled by card index) so multiple schedules
// for one strategy are visually distinct at a glance. Subtle alpha tints +
// a stronger left-border accent; both legible in light and dark themes.
const SCHEDULE_TINTS: Array<{ bg: string; bd: string }> = [
  { bg: 'rgba(33,150,243,0.09)',  bd: 'rgba(33,150,243,0.7)'  }, // blue
  { bg: 'rgba(156,39,176,0.09)',  bd: 'rgba(156,39,176,0.7)'  }, // purple
  { bg: 'rgba(0,150,136,0.10)',   bd: 'rgba(0,150,136,0.75)'  }, // teal
  { bg: 'rgba(255,152,0,0.10)',   bd: 'rgba(255,152,0,0.75)'  }, // amber
  { bg: 'rgba(233,30,99,0.09)',   bd: 'rgba(233,30,99,0.7)'   }, // pink
  { bg: 'rgba(76,175,80,0.10)',   bd: 'rgba(76,175,80,0.75)'  }, // green
];

// Draft schedule = a row the user is building before first save. Has a
// temporary client-side id (`negative number`); after save we replace it
// with the persisted row.
const draftId = (() => { let n = 0; return () => --n; })();

interface RowState {
  id: number;                       // negative = draft, positive = persisted
  watchlistId: number | '';
  intervalMinutes: number;
  scheduleType: ScheduleType;
  scheduleValue: number;
  scheduleTimeOfDay: string;
  enabled: boolean;
  routeDashboard: boolean;
  routeTelegram: boolean;
  /** TradeDesk — per-scan Auto-Trade switch. */
  autoTrade: boolean;
  // Read-only display fields from the server.
  lastRunAt?: string | null;
  lastRunStatus?: string | null;
  lastRunError?: string | null;
  lastSignalsFound?: number | null;
  nextRunAt?: string | null;
}

function rowFromSchedule(s: ScheduledScan): RowState {
  return {
    id: s.id,
    watchlistId: s.watchlistId,
    intervalMinutes: s.intervalMinutes,
    scheduleType: s.scheduleType,
    scheduleValue: s.scheduleValue,
    scheduleTimeOfDay: s.scheduleTimeOfDay || '09:30',
    enabled: !!s.enabled,
    routeDashboard: !!s.routeDashboard,
    routeTelegram: !!s.routeTelegram,
    autoTrade: (s as any).autoTrade !== false,
    lastRunAt: s.lastRunAt,
    lastRunStatus: s.lastRunStatus,
    lastRunError: s.lastRunError,
    lastSignalsFound: s.lastSignalsFound,
    nextRunAt: s.nextRunAt,
  };
}

function newDraftRow(): RowState {
  return {
    id: draftId(),
    watchlistId: '',
    intervalMinutes: 15,
    scheduleType: 'HOUR',
    scheduleValue: 1,
    scheduleTimeOfDay: '09:30',
    enabled: true,
    routeDashboard: true,
    routeTelegram: false,
    autoTrade: true,
  };
}

const ScheduledScanPanel: React.FC<Props> = ({
  strategyId, watchlists, intervalOptions, minutesLabel, cadenceFor, autoTradeRefusedReason, onChange,
}) => {
  /** The distinct exchanges a schedule's watchlist actually contains. The tick
   *  cadence depends on them: the bucket grid's phase differs between a 24x7
   *  venue and a session venue, so suggesting the safe-for-everything answer to
   *  a Delta-only watchlist just makes it tick more often than it needs to. */
  const exchangesOf = useCallback((watchlistId: number | ''): string[] => {
    const wl = watchlists.find(w => w.id === Number(watchlistId));
    if (!wl?.symbols?.length) return [];
    return Array.from(new Set(wl.symbols.map(s => (s.exchange || '').toUpperCase()).filter(Boolean)));
  }, [watchlists]);

  const [rows, setRows] = useState<RowState[]>([]);
  const [loading, setLoading] = useState(false);
  // Per-row saving flags so a save spinner on one row doesn't lock the
  // others.
  const savingRef = useRef<Record<number, boolean>>({});
  const [, force] = useState(0);
  const tick = () => force(n => n + 1);

  const fireOnChange = useCallback((nextRows: RowState[]) => {
    const persisted = nextRows.filter(r => r.id > 0);
    // Cast back to ScheduledScan shape for the callback.
    const all: ScheduledScan[] = persisted.map(r => ({
      id: r.id,
      // The other fields the callback cares about — the parent only
      // reads `enabled` today. Add more fields if/when needed.
      strategyId,
      userId: 0,
      watchlistId: Number(r.watchlistId) || 0,
      intervalMinutes: r.intervalMinutes,
      scheduleType: r.scheduleType,
      scheduleValue: r.scheduleValue,
      scheduleTimeOfDay: r.scheduleTimeOfDay,
      enabled: r.enabled,
      routeDashboard: r.routeDashboard,
      routeTelegram: r.routeTelegram,
      autoTrade: r.autoTrade,
      lastRunAt: r.lastRunAt || null,
      lastRunStatus: r.lastRunStatus as any || null,
      lastRunError: r.lastRunError || null,
      lastSignalsFound: r.lastSignalsFound || 0,
      nextRunAt: r.nextRunAt || null,
      createdAt: '',
      updatedAt: '',
    }));
    onChange?.(all[0] || null, all);
  }, [onChange, strategyId]);

  const load = useCallback(async () => {
    if (!strategyId) return;
    setLoading(true);
    try {
      const items = await strategyScanService.listSchedules(strategyId);
      const next = items.map(rowFromSchedule);
      setRows(next);
      fireOnChange(next);
    } catch {
      setRows([]);
      fireOnChange([]);
    } finally {
      setLoading(false);
    }
  }, [strategyId, fireOnChange]);

  useEffect(() => { load(); }, [load]);

  // Refresh every 20s so last-run / next-run / status update silently.
  useEffect(() => {
    if (!strategyId) return;
    const t = setInterval(async () => {
      try {
        const items = await strategyScanService.listSchedules(strategyId);
        // Only update the read-only fields on rows the user isn't editing —
        // preserve any in-progress edits.
        setRows(prev => prev.map(r => {
          if (r.id <= 0) return r; // draft — leave alone
          const fresh = items.find(s => s.id === r.id);
          if (!fresh) return r;
          return {
            ...r,
            lastRunAt: fresh.lastRunAt,
            lastRunStatus: fresh.lastRunStatus,
            lastRunError: fresh.lastRunError,
            lastSignalsFound: fresh.lastSignalsFound,
            nextRunAt: fresh.nextRunAt,
            enabled: fresh.enabled, // user may have toggled in another tab
          };
        }));
      } catch { /* ignore */ }
    }, 20000);
    return () => clearInterval(t);
  }, [strategyId]);

  const update = (id: number, patch: Partial<RowState>) =>
    setRows(prev => prev.map(r => r.id === id ? { ...r, ...patch } : r));

  const handleAdd = () => setRows(prev => [...prev, newDraftRow()]);

  const handleSave = async (row: RowState) => {
    if (!row.watchlistId) { toast.error('Select a watchlist'); return; }
    if (row.scheduleType === 'DAY' && !/^\d{2}:\d{2}$/.test(row.scheduleTimeOfDay)) {
      toast.error('Enter a valid HH:MM time'); return;
    }
    savingRef.current[row.id] = true; tick();
    try {
      const payload: UpsertSchedulePayload = {
        watchlistId: Number(row.watchlistId),
        intervalMinutes: row.intervalMinutes,
        scheduleType: row.scheduleType,
        scheduleValue: row.scheduleValue,
        scheduleTimeOfDay: row.scheduleType === 'DAY' ? row.scheduleTimeOfDay : null,
        routeDashboard: row.routeDashboard,
        routeTelegram: row.routeTelegram,
        autoTrade: row.autoTrade,
        enabled: row.enabled,
      };
      const saved = row.id > 0
        ? await strategyScanService.updateScheduleById(strategyId, row.id, payload)
        : await strategyScanService.createSchedule(strategyId, payload);
      setRows(prev => {
        const next = prev.map(r => r.id === row.id ? rowFromSchedule(saved) : r);
        fireOnChange(next);
        return next;
      });
      toast.success(row.id > 0 ? 'Schedule updated' : 'Schedule created');
    } catch (err: any) {
      toast.error(err?.response?.data?.error?.message || 'Failed to save schedule');
    } finally {
      savingRef.current[row.id] = false; tick();
    }
  };

  const handleToggle = async (row: RowState, enabled: boolean) => {
    if (row.id <= 0) { update(row.id, { enabled }); return; } // draft — local only
    try {
      const saved = await strategyScanService.toggleScheduleById(strategyId, row.id, enabled);
      setRows(prev => {
        const next = prev.map(r => r.id === row.id ? rowFromSchedule(saved) : r);
        fireOnChange(next);
        return next;
      });
      toast.success(enabled ? 'Schedule resumed' : 'Schedule paused');
    } catch (err: any) {
      toast.error(err?.response?.data?.error?.message || 'Failed to toggle');
    }
  };

  const handleDelete = async (row: RowState) => {
    if (row.id <= 0) {
      // Draft — just drop locally.
      setRows(prev => prev.filter(r => r.id !== row.id));
      return;
    }
    if (!window.confirm('Delete this scheduled scan? This stops its future auto-scans.')) return;
    try {
      await strategyScanService.deleteScheduleById(strategyId, row.id);
      setRows(prev => {
        const next = prev.filter(r => r.id !== row.id);
        fireOnChange(next);
        return next;
      });
      toast.success('Schedule deleted');
    } catch {
      toast.error('Failed to delete');
    }
  };

  return (
    <Card sx={{ mb: 2 }}>
      <CardHeader
        title={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
            <Typography variant="h6" fontWeight={700}>Scheduled Scans</Typography>
            <Chip
              label={`${rows.filter(r => r.id > 0 && r.enabled).length} active`}
              size="small"
              color={rows.some(r => r.id > 0 && r.enabled) ? 'success' : 'default'}
              sx={{ fontWeight: 700 }}
            />
          </Box>
        }
        subheader="Configure one or more independent schedules per strategy. Each runs on its own cadence with its own watchlist + analytical interval."
      />
      <CardContent>
        {loading ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CircularProgress size={18} />
            <Typography variant="body2" color="text.secondary">Loading schedules…</Typography>
          </Box>
        ) : (
          <>
            {rows.length === 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                No schedules yet. Click <strong>Add Schedule</strong> below to create one.
              </Typography>
            )}

            {rows.map((row, idx) => {
              const saving = !!savingRef.current[row.id];
              const isDraft = row.id <= 0;
              // Per-schedule colour so multiple schedules read as clearly
              // distinct cards. Subtle alpha tints work in both light & dark.
              const tint = SCHEDULE_TINTS[idx % SCHEDULE_TINTS.length];
              return (
                <Box key={row.id} sx={{
                  border: '1px solid', borderColor: isDraft ? 'warning.light' : tint.bd,
                  borderLeft: isDraft ? '1px solid' : `5px solid ${tint.bd}`,
                  borderLeftColor: isDraft ? 'warning.light' : undefined,
                  borderRadius: 1, p: 1.5, mb: 2,
                  bgcolor: isDraft ? 'action.hover' : tint.bg,
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                    <Typography variant="subtitle2" fontWeight={700}>
                      {isDraft ? `New Schedule (draft)` : `Schedule #${idx + 1}`}
                    </Typography>
                    {!isDraft && (
                      <Chip
                        label={row.enabled ? 'ACTIVE' : 'PAUSED'}
                        size="small"
                        color={row.enabled ? 'success' : 'default'}
                        sx={{ fontWeight: 700 }}
                      />
                    )}
                    {!isDraft && row.lastRunStatus === 'RUNNING' && (
                      <Chip label="RUNNING" size="small" color="info" />
                    )}
                    {!isDraft && (
                      row.enabled ? (
                        <Button
                          size="small" variant="outlined" color="warning"
                          startIcon={<PauseIcon />}
                          onClick={() => handleToggle(row, false)}
                        >Pause</Button>
                      ) : (
                        <Button
                          size="small" variant="contained" color="success"
                          startIcon={<PlayArrowIcon />}
                          onClick={() => handleToggle(row, true)}
                        >Resume</Button>
                      )
                    )}
                    <Box sx={{ flex: 1 }} />
                    <Button
                      size="small" variant="outlined" color="error" startIcon={<DeleteIcon />}
                      onClick={() => handleDelete(row)}
                    >
                      {isDraft ? 'Discard' : 'Delete'}
                    </Button>
                  </Box>

                  <Grid container spacing={2}>
                    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Watchlist</InputLabel>
                        <Select
                          value={row.watchlistId}
                          label="Watchlist"
                          onChange={e => {
                            // The cadence depends on the venues in the watchlist
                            // (a 24x7 grid and a session grid have different
                            // phases), so picking the watchlist AFTER the
                            // interval used to leave the wrong venue's answer
                            // standing. Re-derive, but only when it actually
                            // differs, so a hand-set cadence is not overwritten
                            // for no reason.
                            const wid = e.target.value as number;
                            const sug = cadenceFor?.(row.intervalMinutes, exchangesOf(wid));
                            update(row.id, sug && (sug.scheduleType !== row.scheduleType || sug.scheduleValue !== row.scheduleValue)
                              ? { watchlistId: wid, scheduleType: sug.scheduleType, scheduleValue: sug.scheduleValue }
                              : { watchlistId: wid });
                          }}
                        >
                          <MenuItem value="">—</MenuItem>
                          {watchlists.map(wl => (
                            <MenuItem key={wl.id} value={wl.id} divider={!!wl.systemKind}
                              data-testid={wl.systemKind === 'FAVOURITES' ? 'schedule-wl-favourites' : undefined}>
                              {wl.name} ({wl.symbols?.length || 0})
                            </MenuItem>
                          ))}
                        </Select>
                        {watchlists.find(w => w.id === Number(row.watchlistId))?.systemKind === 'FAVOURITES' && (
                          <FormHelperText data-testid="schedule-wl-favourites-hint">
                            Follows your starred symbols: each run scans what is starred at that moment.
                          </FormHelperText>
                        )}
                      </FormControl>
                    </Grid>
                    <Grid size={{ xs: 6, sm: 3, md: 2 }}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Interval</InputLabel>
                        <Select
                          value={row.intervalMinutes}
                          label="Interval"
                          onChange={e => {
                            const m = Number(e.target.value);
                            // Auto-fill the cadence that covers this bar size.
                            // For a builtin (all day-divisors) there is nothing
                            // to suggest and the user's cadence is left alone;
                            // for 7H and friends the suggestion is the whole
                            // point, because no cadence can equal the bar. Still
                            // overridable below.
                            const sug = cadenceFor?.(m, exchangesOf(row.watchlistId));
                            update(row.id, sug
                              ? { intervalMinutes: m, scheduleType: sug.scheduleType, scheduleValue: sug.scheduleValue }
                              : { intervalMinutes: m });
                          }}
                        >
                          {intervalOptions.map(m => (
                            <MenuItem key={m} value={m}>{minutesLabel(m)}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    {/* The cadence: HOW OFTEN THE SCAN LOOKS. Deliberately worded
                        so it cannot be read as the bar size — "Interval" above is
                        the candle, this is the clock, and conflating the two is
                        the whole reason a 7H scan was impossible before.

                        The number comes BEFORE the unit so the pair reads as one
                        sentence. It used to be a Select labelled "Every" followed
                        by a bare number field labelled "N", which reads backwards
                        and tells you nothing on its own. */}
                    <Grid size={{ xs: 6, sm: 3, md: 2 }}>
                      <TextField
                        fullWidth size="small" type="number" label="Check every"
                        value={row.scheduleValue}
                        onChange={e => update(row.id, { scheduleValue: clampCadence(Number(e.target.value), row.scheduleType) })}
                        inputProps={{ min: 1, max: cadenceMax(row.scheduleType) }}
                        helperText={`1–${cadenceMax(row.scheduleType)}`}
                      />
                    </Grid>
                    <Grid size={{ xs: 6, sm: 3, md: 2 }}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Unit</InputLabel>
                        <Select
                          value={row.scheduleType}
                          label="Unit"
                          onChange={e => {
                            // Re-clamp: the server caps MINUTE at 59 and HOUR at
                            // 23, so switching Days/100 to Minutes without this
                            // sent 100 and came back a 400 the user had to
                            // decode from a toast.
                            const t = e.target.value as ScheduleType;
                            update(row.id, { scheduleType: t, scheduleValue: clampCadence(row.scheduleValue, t) });
                          }}
                        >
                          <MenuItem value="MINUTE">minutes</MenuItem>
                          <MenuItem value="HOUR">hours</MenuItem>
                          <MenuItem value="DAY">days</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    {row.scheduleType === 'DAY' && (
                      <Grid size={{ xs: 6, sm: 3, md: 2 }}>
                        <TextField
                          fullWidth size="small" type="time" label="At (IST)"
                          value={row.scheduleTimeOfDay}
                          onChange={e => update(row.id, { scheduleTimeOfDay: e.target.value })}
                          InputLabelProps={{ shrink: true }}
                        />
                      </Grid>
                    )}
                  </Grid>

                  {(() => {
                    // Say plainly how this interval is actually driven, and flag
                    // a cadence coarser than the suggestion — that is the one
                    // setting here that can silently MISS bar closes.
                    //
                    // The sentence is built from the SAME suggestion the field
                    // above holds. It used to come from the server, computed for
                    // a 24x7 venue, so on an NSE watchlist the prose named one
                    // cadence while the control held another.
                    const m = row.intervalMinutes;
                    const sug = cadenceFor?.(m, exchangesOf(row.watchlistId));
                    if (!sug) return null;
                    const mins = (t: ScheduleType, v: number) => (t === 'DAY' ? 1440 : t === 'HOUR' ? v * 60 : v);
                    const tooCoarse = mins(row.scheduleType, row.scheduleValue) > mins(sug.scheduleType, sug.scheduleValue);
                    const label = minutesLabel(m);
                    const words = cadenceWords(sug);
                    const how = isScanEligible(m)
                      ? `${label} bars close on the scan grid, so checking ${words} lands on every close.`
                      : `${label} does not divide a day (1440 % ${m} = ${DAY % m}), so no cadence can equal it. `
                        + `Checking ${words} covers every close instead — the scan evaluates CLOSED bars only `
                        + `and logs at most one signal per bar, so the extra looks cost a data refresh and nothing else.`;
                    return (
                      <Typography variant="caption" sx={{ display: 'block', mt: 1, color: tooCoarse ? 'warning.main' : 'text.secondary' }}>
                        {tooCoarse
                          ? `Coarser than ${words}, so some ${label} closes will be skipped. ${how}`
                          : how}
                      </Typography>
                    );
                  })()}

                  <Box sx={{ mt: 1.5, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                    <FormControlLabel
                      control={<Switch checked={row.routeDashboard} onChange={e => update(row.id, { routeDashboard: e.target.checked })} />}
                      label="Route to Dashboard (Recent Alerts)"
                    />
                    <FormControlLabel
                      control={<Switch checked={row.routeTelegram} onChange={e => update(row.id, { routeTelegram: e.target.checked })} />}
                      label="Send Telegram notification"
                    />
                    <Tooltip title={autoTradeRefusedReason
                      || "TradeDesk — when ON (and global Auto Trade is ON), this scan's signals place real orders through the unified post-signal engine. When OFF the scan only routes signals (Dashboard/Telegram) — useful for watching a strategy before trusting it with money. Independent of the Dashboard route."} arrow>
                      <FormControlLabel
                        disabled={!!autoTradeRefusedReason}
                        control={<Switch color="warning"
                          disabled={!!autoTradeRefusedReason}
                          checked={!autoTradeRefusedReason && row.autoTrade}
                          onChange={e => update(row.id, { autoTrade: e.target.checked })} />}
                        label={autoTradeRefusedReason ? 'Auto-Trade — refused by this engine' : 'Auto-Trade this scan'}
                      />
                    </Tooltip>
                  </Box>

                  <Box sx={{ mt: 1.5, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <Button
                      variant="contained" size="small" startIcon={<SaveIcon />}
                      onClick={() => handleSave(row)}
                      disabled={saving || !row.watchlistId}
                    >
                      {saving ? 'Saving…' : (isDraft ? 'Save Schedule' : 'Update Schedule')}
                    </Button>
                  </Box>

                  {!isDraft && (
                    <>
                      <Divider sx={{ my: 1.5 }} />
                      <Grid container spacing={2}>
                        <Grid size={{ xs: 6, sm: 3 }}>
                          <Typography variant="caption" color="text.secondary">Last Run</Typography>
                          <Typography variant="body2">{formatTs(row.lastRunAt)}</Typography>
                        </Grid>
                        <Grid size={{ xs: 6, sm: 3 }}>
                          <Typography variant="caption" color="text.secondary">Last Status</Typography>
                          <Typography variant="body2">
                            <Chip
                              label={row.lastRunStatus || '—'}
                              size="small"
                              color={
                                row.lastRunStatus === 'SUCCESS' ? 'success' :
                                row.lastRunStatus === 'FAILURE' ? 'error' :
                                row.lastRunStatus === 'RUNNING' ? 'info' : 'default'
                              }
                              sx={{ fontSize: '0.65rem', height: 20 }}
                            />
                          </Typography>
                        </Grid>
                        <Grid size={{ xs: 6, sm: 3 }}>
                          <Typography variant="caption" color="text.secondary">Signals Found (last run)</Typography>
                          <Typography variant="body2">{row.lastSignalsFound ?? 0}</Typography>
                        </Grid>
                        <Grid size={{ xs: 6, sm: 3 }}>
                          <Typography variant="caption" color="text.secondary">Next Run</Typography>
                          <Typography variant="body2">{formatTs(row.nextRunAt)}</Typography>
                        </Grid>
                        {row.lastRunError && (
                          <Grid size={{ xs: 12 }}>
                            <Alert severity="warning" sx={{ py: 0.5 }}>
                              Last error: {row.lastRunError}
                            </Alert>
                          </Grid>
                        )}
                      </Grid>
                    </>
                  )}
                </Box>
              );
            })}

            <Button
              variant="outlined" size="small" startIcon={<AddIcon />}
              onClick={handleAdd}
            >
              Add Schedule
            </Button>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default ScheduledScanPanel;
